import React, { useState } from 'react';

import Button, { LoadingButton } from '@atlaskit/button';
import Modal, {
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  ModalTransition,
} from '@atlaskit/modal-dialog';

import { Folder, TestCaseInFolder } from '@qality/common-model';
import { FlagService, FolderService } from '@qality/common-service';

import { useTestCaseRepositoryIssuesContext } from '../TestCaseRepositoryIssuesProvider';
import { ALL_TEST_CASES } from '../TestCaseRepositoryPage.const';
import { ModalTextStyled } from './DeleteFromFolderModal.styled';

type Props = {
  onDeleteFromFolder: (
    folderId: Folder['id'],
    testCasesInFolder: Array<TestCaseInFolder>
  ) => void;
  onClose: () => void;
  testCasesInFolder: Array<TestCaseInFolder>;
  folder: Folder;
};

export default function DeleteFromFolderModal({
  onDeleteFromFolder,
  onClose,
  testCasesInFolder,
  folder,
}: Props): JSX.Element {
  const { deleteTestCasesFromFolder } = useTestCaseRepositoryIssuesContext();

  const [isLoading, setIsLoading] = useState(false);

  const handleDeleteFromFolder = async () => {
    const testCasesInFolderIds = testCasesInFolder.map(
      (testCaseInFolder) => testCaseInFolder.id
    );

    try {
      setIsLoading(true);
      FolderService.deleteTestCasesFromFolder(testCasesInFolderIds, folder.id);
      onDeleteFromFolder(folder.id, testCasesInFolder);
      deleteTestCasesFromFolder(testCasesInFolder);
      FlagService.createSuccessFlag('Success', messages.successFlag);
    } catch (error) {
      FlagService.createErrorFlag('Error', messages.errorFlag);
    } finally {
      setIsLoading(false);
      onClose();
    }
  };

  const messages =
    testCasesInFolder.length === 1
      ? {
          successFlag: 'Successfully deleted test case from folder',
          errorFlag:
            'We could not removed selected test case from folder. ' +
            'Please try again. If the problem persists, feel free to contact our support.',
          modalTitle: 'Remove Test Case from this folder',
          modalText: `After removing the Test Case from this folder, it will still be available in the "${ALL_TEST_CASES}" tab, and in any other folder this test case was added.`,
          removeButton: 'Remove Test Case',
        }
      : {
          successFlag: 'Successfully deleted test cases from folder',
          errorFlag:
            'We could not removed selected test cases from folder. ' +
            'Please try again. If the problem persists, feel free to contact our support.',
          modalTitle: 'Remove Test Cases from this fold',
          modalText: `After removing the Test Cases from this folder, they will still be available in the “${ALL_TEST_CASES}” tab, and in any other folder those Test Cases were added.`,
          removeButton: 'Remove Test Cases',
        };

  return (
    <ModalTransition>
      <Modal
        onClose={onClose}
        shouldCloseOnOverlayClick={false}
        shouldCloseOnEscapePress={true}
        shouldScrollInViewport
        width={400}
      >
        <ModalHeader>
          <ModalTitle appearance='danger'>{messages.modalTitle}</ModalTitle>
        </ModalHeader>
        <ModalBody>
          <ModalTextStyled>{messages.modalText}</ModalTextStyled>
        </ModalBody>
        <ModalFooter>
          <Button appearance='subtle' onClick={onClose}>
            Cancel
          </Button>
          <LoadingButton
            isLoading={isLoading}
            appearance='danger'
            onClick={handleDeleteFromFolder}
          >
            {messages.removeButton}
          </LoadingButton>
        </ModalFooter>
      </Modal>
    </ModalTransition>
  );
}
